// Phase-2 Slice-23 — connection lifecycle for the navigational identifier
// index.
//
// Subscribes to the renderer-side connection store and:
//   - On a `'connected'` transition: SCHEDULES the index for the connection's
//     CONFIGURED database. It does NOT build (nor even create the index) on
//     connect: the schedule waits for the engine's schema snapshot to finish
//     and for an idle moment (`navIndexSchedule.ts` explains why, and holds
//     the two waits), and then for the connection's CASE POLICY
//     (`navLifecycleDatabaseResolution.ts`). Only then are the
//     `IdentifierIndex` and its DDL-invalidator registration (Slice 22) created
//     and `buildIndexForConnection(...)` run.
//   - On a `'connected' → 'disconnected'/'error'/absent` transition: cancels
//     every pending or in-progress build, clears every index, disposes the
//     invalidator registrations, forgets the case policy, and forgets the
//     entry. Re-connecting starts fresh indices from scratch.
//
// ONE INDEX PER DATABASE IDENTITY — and what a database identity IS depends on
// the engine (`databaseIdentity.ts`). A SQL Server or PostgreSQL connection
// spans many databases and every reader — the References tab, Peek References,
// Rename — targets one of them (the Object-Explorer node's database, or the
// query tab's); a MySQL, Oracle or SQLite connection builds the SAME index
// whatever database is named, so it holds exactly one. Before 2026-09-12 the
// lifecycle held a single index per connection, built for the configured
// database only (the login default when none was configured), so "Find
// references…" on a table in any other database reported nothing. Now
// `indexReadyFor(connectionId, database)` builds an index for that database's
// identity ON DEMAND — immediately, skipping the schedule gates, because a
// user is waiting on it — and `getIdentifierIndexFor` hands back the index for
// that identity. The configured database keeps the deferred connect-time
// schedule.
//
// A connection configured with NO database is the case where the identity has
// to be LEARNT: the schedule would file the login default under `''` while the
// Object Explorer and every query tab name that same database explicitly
// (`master`), giving one catalog two indices, two DDL registrations and two
// References tabs. The case-policy gate therefore resolves `''` to the login
// default's real name once per connection (`rememberDefaultDatabase`), on the
// multi-database engines only — `mysql` / `oracle` / `sqlite` already key every
// request under one identity, so there is nothing there to disambiguate.
//
// CASE POLICY FIRST. Every key above — the index's object keys AND the
// database identity — depends on an engine setting the app has to ask the
// server for (`navCasePolicy.ts`): MySQL's `@@lower_case_table_names`, SQL
// Server's server collation, and on SQL Server the collation of EACH DATABASE
// (object and schema names follow it, and a `_CI_` instance can hold a `_CS_`
// database). An index keyed on a guess cannot be repaired (a kind's key policy
// freezes at its first record), so nothing is created until both answers are
// in: one probe per connection shared by every database, one more per database
// identity, and `indexReadyFor` REJECTS when either fails — leaving no entry
// and no invalidator registration, so a DDL in that window finds nothing to
// refresh and the build that follows reads the catalog anyway. The next
// request probes again.
//
// `mock` connections are skipped — `listParseableObjects` already returns `[]`
// for the mock engine, but skipping at this layer keeps `__TEST_STORES__`
// state predictable and avoids needless invalidator registrations during E2E
// runs that mostly exercise the mock engine.
//
// The lifecycle is module state (one map across the renderer); the connection
// store is itself a singleton, so collocating the lifecycle map with it is
// safe. Tests reset state via `__resetNavigationalLifecycleForTest()`.
//
// The build stays non-blocking: the user sees their connection appear in the
// OE tree immediately, and find-references / rename gate on
// `indexReadyFor(connectionId, database)` — which creates that database's
// entry itself and builds it at once, so the wait is for the build, never for
// the connect-time schedule to expire.
//
// A build that does not FINISH never fulfils readiness, and the two ways it can
// fail mean different things (`runBuild`):
//   - CANCELLED — a teardown flips `shouldAbort` and clears the index, so the
//     builder returns early and the promise the waiting References tab holds
//     has nothing behind it. Fulfilling it would report an emptied index — or,
//     once the same id has reconnected, the NEW lifecycle's not-yet-built one —
//     as a finished search.
//   - FAILED — the catalog read rejected. The index keeps whatever the builder
//     recorded (a real subset) and stays installed with its DDL registration,
//     so refresh and clear-on-disconnect keep working, but it is marked PARTIAL
//     and readiness rejects `'build-failed'`: reporting "no references found"
//     over a fraction of the catalog is the false negative this area exists to
//     remove. The next `indexReadyFor` REBUILDS into the same index object —
//     EMPTIED first, because the builder only records: keeping the failed
//     attempt's records would hold every object it reached twice and leave the
//     partial flag set over a catalog the retry now covers in full.
// Both rejections are the typed error every other refusal uses
// (`navIndexUnavailable.ts`), so the caller can tell "nothing to search" from
// "nothing was searched".
//
// `casePolicyReadyFor(connectionId, database)` exposes the CASE-POLICY half of
// that wait on its own, for a surface that has to BIND a name before it
// searches (rename re-resolves its cursor target behind it — see there).
//
// This file is the FACADE every importer uses; its internals live in four
// siblings:
//   - `navLifecycleRegistry.ts` — the record shapes (`LifecycleDeps`,
//     `ConnectionEntry`, `DatabaseIndexEntry`), the `liveLifecycle` map,
//     `assertStillLive` / `isOrdinaryDisconnect`, and `tearDownConnection`.
//   - `navLifecycleDatabaseResolution.ts` — the two-half case-policy gate
//     (`ensureFullCasePolicyKnown`), `resolveDatabase` (and its reader-facing
//     `resolvedDatabaseFor`, re-exported below), the login-default probe
//     `rememberDefaultDatabase` rides on (`probeDefaultDatabase`), and
//     `routingDatabaseFor` — which database an entry's catalog queries RUN IN,
//     as opposed to which one the request is ABOUT. The two differ only on the
//     engines where one index spans the whole connection: the name routes the
//     query without changing what the index holds, so the entry uses the
//     CONNECTION's own rather than whichever requester happened to create it.
//   - `navLifecycleBuild.ts` — `startDatabaseIndex` (index + DDL registration),
//     `runBuild`, and the deferred connect-time `scheduleConfiguredDatabase`.
//   - `navLifecycleAttach.ts` — the production `LifecycleDeps`, the store
//     subscription and the reconcile behind `attachNavigationalLifecycle`,
//     re-exported below.

import type { IdentifierIndex } from '../types';

import { databaseIdentityKey } from './databaseIdentity';
import { navIndexUnavailable } from './navIndexUnavailable';
import { assertStillLive, liveLifecycle, tearDownConnection } from './navLifecycleRegistry';
import { ensureFullCasePolicyKnown, resolveDatabase } from './navLifecycleDatabaseResolution';
import { runBuild, startDatabaseIndex } from './navLifecycleBuild';

export type { LifecycleDeps } from './navLifecycleRegistry';
export { attachNavigationalLifecycle, type AttachOptions } from './navLifecycleAttach';
/** The database name a request is actually keyed under — `''` / omitted
 *  resolved to the connection's configured database, or (when the dialog named
 *  none) to the login default the case-policy gate learnt. Readers that COMPARE
 *  two database names go through this first; see there. */
export { resolvedDatabaseFor } from './navLifecycleDatabaseResolution';

/**
 * Build the index for `database` (the connection's configured database when
 * omitted) — learning the connection's case policy and creating that
 * database's index first if this is the first request for it — and resolve
 * once the build has FINISHED. This is the on-demand path: a user is waiting,
 * so the two schedule gates are skipped outright.
 *
 * Rejects when no connection is registered (or it went away mid-probe), when
 * the case policy cannot be learnt, when the build this wait belongs to was
 * CANCELLED by a teardown, and when it FAILED. Every rejection is a
 * `NavIndexUnavailableError` carrying the reason (`navIndexUnavailable.ts`),
 * because the four mean different things to the surface that was waiting: only
 * the first is an honest "there is nothing to search". A request that arrives
 * after a failed build starts a fresh one into the same index rather than
 * inheriting the failure.
 */
export async function indexReadyFor(connectionId: string, database?: string): Promise<void> {
  const entry = liveLifecycle.get(connectionId);
  if (!entry) throw navIndexUnavailable('no-lifecycle', `no nav index for connection ${connectionId}`);
  const target = await ensureFullCasePolicyKnown(connectionId, entry, database);
  // The gate checks liveness as its LAST act, but THIS continuation runs in a
  // later microtask — and a disconnect continuation scheduled in between would
  // otherwise be followed by `startDatabaseIndex` installing an index and a DDL
  // registration into a map teardown has already emptied: orphans nothing
  // disposes, feeding a build that keeps paging a catalog for a connection that
  // is gone (and a readiness promise that never settles).
  assertStillLive(connectionId, entry);
  const existing = entry.databases.get(databaseIdentityKey(connectionId, entry.engineId, target));
  if (!existing) return startDatabaseIndex(connectionId, entry, target).ready;
  // A previous build REJECTED: its records and its DDL registration are still
  // installed, so retry into the same index rather than re-reporting the
  // failure forever. The next user gesture is the retry — no background loop,
  // the same rule the case-policy probes follow.
  if (existing.buildFailed) existing.ready = runBuild(connectionId, entry, existing);
  return existing.ready;
}

/**
 * Resolve once the case policy for `database` is KNOWN — the gate
 * {@link indexReadyFor} opens before it keys anything, on its own and without
 * creating (or building) an index.
 *
 * A surface that BINDS an identifier before it searches needs exactly this and
 * not the build: which tree node a dotted chain names depends on how the server
 * compares names, not on what the index contains. Rename re-resolves its cursor
 * target behind this wait (`rename/renameProvider.ts`), which costs at most the
 * two one-row probes the index wait would have paid anyway — they are shared,
 * so whichever asks first pays.
 *
 * Rejects with the same typed reasons `indexReadyFor` uses, minus the ones only
 * a build can produce.
 */
export async function casePolicyReadyFor(connectionId: string, database?: string): Promise<void> {
  const entry = liveLifecycle.get(connectionId);
  if (!entry) throw navIndexUnavailable('no-lifecycle', `no nav index for connection ${connectionId}`);
  await ensureFullCasePolicyKnown(connectionId, entry, database);
}

/** Synchronous handle for callers (find-references, rename) AFTER awaiting
 *  `indexReadyFor(connectionId, database)`. Returns `null` when no connection
 *  is registered, or when no index has been requested for that database. */
export function getIdentifierIndexFor(connectionId: string, database?: string): IdentifierIndex | null {
  const entry = liveLifecycle.get(connectionId);
  if (!entry) return null;
  const key = databaseIdentityKey(connectionId, entry.engineId, resolveDatabase(entry, database));
  return entry.databases.get(key)?.index ?? null;
}

/** Test-only — wipe the live-lifecycle map between unit cases. */
export function __resetNavigationalLifecycleForTest(): void {
  for (const cid of [...liveLifecycle.keys()]) tearDownConnection(cid);
}

/** Test-only — peek at registered connection IDs. */
export function __navigationalLifecycleConnectionIdsForTest(): readonly string[] {
  return Array.from(liveLifecycle.keys());
}

/** Test-only — the databases a connection currently holds an index for, in
 *  request order (the configured database first). On MySQL / Oracle / SQLite
 *  that is ONE entry per connection, and its database is the connection's own
 *  — the value its catalog queries route to (`routingDatabaseFor`). */
export function __navigationalLifecycleDatabasesForTest(connectionId: string): readonly string[] {
  const entry = liveLifecycle.get(connectionId);
  if (!entry) return [];
  return Array.from(entry.databases.values(), (db) => db.database);
}
